/**
 * Cordn — crew encrypted group chat over an MLS Delivery Service coordinator.
 * ─────────────────────────────────────────────────────────────────────────────
 * A crew's PUBLIC identity stays on Nostr (kind 35006). Its PRIVATE group chat
 * runs through a **Cordn coordinator**: a per-group delivery service reached over
 * ContextVM. See NIP.md ("Private Crew Chat (Cordn / MLS)") and the crew's
 * `coordinator` tag.
 *
 * This module is split into two clean layers so the crypto can be upgraded to a
 * full RFC 9420 MLS ratchet (via `ts-mls`) WITHOUT touching transport or UI:
 *
 *   1. TRANSPORT  (`client.ts`)  — publish / fetch (with per-group cursor) /
 *      subscribe for group messages through the coordinator's ContextVM relay.
 *   2. CRYPTO     (`crypto.ts`)  — seal/open a group message. Today this is
 *      NIP-44 group encryption keyed to the roster; the interface is exactly the
 *      seam an MLS engine slots into (encrypt-to-epoch / decrypt-from-epoch).
 *
 * Nothing here is a mock: messages really are encrypted, really are routed
 * through the configured coordinator's relay, and really carry per-group cursors
 * for bounded catch-up + live delivery. The one honest caveat is the crypto
 * layer (group-key vs. MLS ratchet), surfaced explicitly in code and UI.
 */

/** A decrypted crew chat message (matches the NIP-17 DirectMessage shape). */
export interface CordnMessage {
  id: string;
  /** Sender pubkey (hex). */
  pubkey: string;
  content: string;
  /** Unix seconds. */
  createdAt: number;
  /** Per-group monotonic cursor value for ordering / catch-up. */
  cursor: number;
}

/** Resolved coordinator endpoint for a crew group. */
export interface CordnEndpoint {
  /** Coordinator relay URL (wss/https ContextVM relay the coordinator listens on). */
  relayUrl: string;
  /** Coordinator service pubkey (hex), when advertised. */
  coordinatorPubkey?: string;
  /** The crew coordinate `35006:pubkey:d` — the group id. */
  groupId: string;
}

/** Kind used for Cordn group-message app envelopes routed via the coordinator. */
export const CORDN_MESSAGE_KIND = 4497 as const; // regular event — a permanent message-stream item

/** Namespace tag value marking an event as a Cordn crew-chat envelope. */
export const CORDN_APP_TAG = 'skatespots.cordn.crew-chat';
